import React from 'react';

import { Text } from 'apps/auth/components/common/Text/Text';
import type { TextColor, TextSize, TextVariant } from 'apps/auth/components/common/Text/Text';
import { resolveArguments } from 'apps/auth/helpers/utils';

interface TextOptions {
    variant?: TextVariant;
    color?: TextColor;
    size?: TextSize;
}

interface InnerMessage {
    key?: string;
    value?: string;
    url?: string;
    isBlank?: boolean;
}

interface ComplexMessage {
    message?: string;
    innerMessages?: InnerMessage[];
}

type MessageArgs = Array<string | number | undefined> | string | number | Record<string, unknown> | null | undefined;

const getHash = (str = ''): number => {
    let hash = 0,
        i: number,
        chr: number;
    if (str.length === 0) return hash;
    for (i = 0; i < str.length; i++) {
        chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0;
    }
    return hash;
};

export class MarkdownService {
    static #regex = /\r?\\n|\r?\n/;

    static parseComplexMessage = (complexMessage: ComplexMessage = {}, options: TextOptions = {}): JSX.Element => {
        const messages = (complexMessage?.message || '').split(this.#regex);
        const innerMessages = complexMessage?.innerMessages || [];
        return (
            <>
                {messages.map((m) => {
                    if (m) {
                        return (
                            <Text key={getHash(m)} variant={options?.variant || 'paragraph'} color={options?.color || 'primary'}>
                                {this.#replaceText(m, innerMessages)}
                            </Text>
                        );
                    }
                })}
            </>
        );
    };

    static splitByParagraph = (message = '', params: MessageArgs = [], options: TextOptions = {}): JSX.Element => {
        const messages = message.split(this.#regex);
        return (
            <>
                {messages.map((m) => {
                    if (m) {
                        return (
                            <Text key={getHash(m)} variant={options?.variant || 'paragraph'} color={options?.color || 'primary'} size={options?.size || 'main'}>
                                {resolveArguments(m, params)}
                            </Text>
                        );
                    }
                })}
            </>
        );
    };

    static removeNewlines = (str = ''): string => {
        return str.replace(this.#regex, ' ');
    };

    static replaceBrWithNewlines = (str = ''): string => {
        return str.replace(/<br\s*\/?>/gi, '\n');
    };

    static #replaceText = (text = '', innerMessages: InnerMessage[] = []): React.ReactNode => {
        let resolveText = text;
        if (Array.isArray(innerMessages) && innerMessages.length > 0) {
            const links = innerMessages.filter((im) => im?.url);
            const planeText = innerMessages.filter((im) => !im?.url);
            if (planeText?.length > 0) {
                planeText.forEach(({ key, value }) => {
                    if (key) {
                        resolveText = resolveText.replaceAll(`[${key}]`, value ?? '');
                    }
                });
            }
            if (links?.length > 0) {
                return this.#replaceLinks(resolveText, links, 0);
            }
        }
        return resolveText;
    };

    static #replaceLinks = (text = '', innerMessages: InnerMessage[] = [], index = 0): React.ReactNode => {
        const im = innerMessages[index];
        const key = `[${im?.key}]`;
        if (im && text.indexOf(key) > 0) {
            const nextIndex = index + 1;
            const newText = text.split(key);
            return newText.map((t, textIndex) => (
                <span key={getHash(t)}>
                    {this.#replaceLinks(t, innerMessages, nextIndex)}
                    {textIndex + 1 !== newText.length && (
                        <a href={im.url} target={im.isBlank ? '_blank' : '_self'} className="link">
                            {im.value}
                        </a>
                    )}
                </span>
            ));
        }
        return text;
    };
}
